"use client";

import {
  Fragment,
  useState,
  useRef,
  type ReactNode,
  type MouseEvent,
  useEffect,
  type FC,
  type Ref,
} from "react";
import {
  Popup,
  Text,
  type TextFieldProps,
  type PopupProps,
  useClickOutside,
  useDebouncedState,
  Icon,
  ProgressBar,
  TextField,
  type Timeout,
  SelectList,
  Flex,
  type FocusTrapProps,
  Card,
  Placeholder,
  nextFrame,
  useLocalState,
  IconButton,
  Message,
  useInitializedEffect,
  mergeRefs,
  useDebug,
} from "@huub-web/ui";
import { mergeClasses, rem } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import type {
  WalterLivingAddress,
  WalterLivingInformation,
} from "../../services/walter-living/types";
import {
  notFoundCTAContainerStyle,
  searchPropertySelectStyle,
  searchStateContainerStyle,
  popupContentStyle,
} from "./SearchPropertySelect.css";
import { getDisplayStreet } from "../../services";
import { SelectedProperty } from "./components/SelectedProperty";

/**
 * Props for the SearchPropertySelect component
 */
export type SearchPropertySelectProps = Omit<
  TextFieldProps,
  "onChange" | "value"
> & {
  /**
   * Callback when an address is selected
   * @param address - The selected address
   * @param element - The element that was selected
   */
  onChange?: (address: WalterLivingAddress | null, element: Element) => void;
  /**
   * Callback when a search is performed
   * @param query - The search query
   * @param element - The element that was searched
   */
  onSearch?: (query: string, element: Element) => void;
  /**
   * Callback when a search is started
   * @param query - The search query
   * @param element - The element that was searched
   */
  onSearchStart?: (query: string) => void;
  /**
   * Function to get search results
   * */
  getSearchResults: (query: string) => Promise<WalterLivingAddress[]>;
  /**
   * Popup configuration
   * */
  popup?: Pick<PopupProps, "placement" | "minWidth" | "maxWidth" | "width">;
  /**
   * Minimum characters to trigger search
   * @default 3
   * */
  minSearchLength?: number;
  /**
   * Debounce delay in milliseconds
   * @default 500
   * */
  debounceDelay?: number;
  /**
   * CTA to display
   * */
  cta?: ReactNode;
  /**
   * Width of the select
   * */
  width?: "stretch";
  /**
   * Selected address
   * */
  selected?: WalterLivingInformation;
  /**
   * Selected address loading
   * */
  selectedLoading?: boolean;
  /**
   * Text to display when no results are found
   * */
  noResultsText?: string;
  /**
   * Callback when the selected address is deleted
   * */
  onDelete?: () => void;
  /**
   * Clear the search query when a property is selected
   * */
  clearOnSelect?: boolean;
  /**
   * Trailing element for the selected property
   * */
  selectedPropertyTrailing?: ReactNode;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * SearchPropertySelect component allows users to search and select properties
 * using the Walter Living API
 */
export const SearchPropertySelect: FC<SearchPropertySelectProps> = ({
  popup = { placement: "bottom", minWidth: rem(300), maxWidth: "100%" },
  debounceDelay = 500,
  placeholder: placeholderProp,
  noResultsText: noResultsTextProp,
  width,
  disabled,
  cta,
  selected,
  selectedLoading,
  className,
  onChange,
  onSearch,
  onSearchStart,
  getSearchResults,
  onDelete,
  clearOnSelect = true,
  selectedPropertyTrailing,
  ref,
  ...textFieldProps
}) => {
  const { t } = useTranslation();
  const { info } = useDebug();

  const placeholder = placeholderProp ?? t("searchPropertySelect.placeholder");
  const noResultsText =
    noResultsTextProp ?? t("searchPropertySelect.noResults");

  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<WalterLivingAddress[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchedOnce, setSearchedOnce] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // useEffect(() => {
  //   info("isLoading changed:", isLoading);
  // }, [isLoading]);

  const [trapFocusMode, setTrapFocusMode] =
    useState<FocusTrapProps["mode"]>("after");

  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  // SSR-safe useLocalState - only use localStorage value after mount
  const [propertyQuery, setPropertyQuery] = useLocalState<string>(
    "propertyQuery",
    "",
  );

  // Search
  // ------------------------------

  const timerRef = useRef<Timeout | null>(null);
  const isInitialSyncRef = useRef(false);

  const handleSearch = async (query: string) => {
    if (!fieldRef.current) {
      return;
    }

    if (!query) {
      setIsLoading(false);
      return;
    }

    onSearchStart?.(query);

    setIsOpen(true);
    setIsLoading(true);
    setError(null);
    setSearchedOnce(true);

    onSearch?.(query, fieldRef.current);

    getSearchResults(query)
      .then((results) => {
        // console.log(results);
        setSearchResults(results ?? []);
      })
      .catch((error) => {
        console.error(error);
        setError(error.message);
      })
      .finally(() => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
          setIsLoading(false);
        }, 500);
      });
  };

  // Debounced state
  // ------------------------------

  const { state: value, setState: setValue } = useDebouncedState<string>(
    propertyQuery ?? "",
    {
      debounced: async (value) => {
        // info("Debounced callback called", {
        //   value,
        //   isInitialSync: isInitialSyncRef.current,
        //   isFocused,
        // });
        // Skip search during initial localStorage sync
        if (!isInitialSyncRef.current) {
          handleSearch(value);
        } else {
          isInitialSyncRef.current = false;
        }
        return value;
      },
      immediate: (value) => {
        // info("Immediate callback called", value);
        setPropertyQuery(value);
        return value;
      },
    },
    debounceDelay,
  );

  // Sync propertyQuery from localStorage to value (for prefill)
  useEffect(() => {
    if (propertyQuery && !value && !isInitialSyncRef.current) {
      // info("Syncing propertyQuery to value", propertyQuery);
      isInitialSyncRef.current = true;
      setValue(propertyQuery);
    }
  }, [propertyQuery]);

  useEffect(() => {
    if (closeButtonRef.current) {
      closeButtonRef.current.style.visibility = value ? "visible" : "hidden";
    }
  }, [value]);

  useInitializedEffect(() => {
    // info("useInitializedEffect triggered", { isFocused, value });
    if (isFocused && value) {
      // info("Triggering search from useInitializedEffect", value);
      onSearchStart?.(value);
    }
  }, [value]);

  // Handle address selection
  // ------------------------------

  const [selectedID, setSelectedID] = useState<string | null>(null);

  const handleAddressSelect = (address: WalterLivingAddress) => {
    if (!fieldRef.current) {
      return;
    }

    onChange?.(address, fieldRef.current);
    setIsOpen(false);
    setSearchedOnce(false);
    if (clearOnSelect) {
      handleClear();
    }
    nextFrame(() => {
      setIsOpen(false);
    });
  };

  // Handle focus
  // ------------------------------

  const handleFocus = () => {
    // info("handleFocus called", {
    // value,/
    // propertyQuery,
    // searchedOnce,
    // isOpen,
    // });
    setIsFocused(true);

    setTrapFocusMode("after");
    if (!isOpen) {
      setIsOpen(true);
    }

    if (value && !searchedOnce) {
      info("Triggering search from handleFocus", value);
      handleSearch(value);
    }
  };

  // Handle outside click
  // ------------------------------

  useClickOutside<HTMLDivElement>([popupRef, fieldRef], () => {
    if (isOpen) {
      setIsOpen(false);
      info("useClickOutside", isOpen);
    }
  });

  // Handle clear
  // ------------------------------

  const handleClear = (e?: MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    e?.stopPropagation();

    setPropertyQuery("");
    setValue("");
    setSearchResults([]);
    setIsOpen(false);
    setSearchedOnce(false);
  };

  const handleDelete = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onDelete?.();
    handleClear();
  };

  const isSelected = !!selected || selectedLoading;

  return (
    <Popup
      ref={popupRef}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      targetRef={mergeRefs<HTMLDivElement>(ref, fieldRef) as any}
      active={isOpen && searchedOnce && !!value && !isSelected}
      onClose={() => {
        setIsOpen(false);
        setTrapFocusMode("after");
      }}
      content={
        <div className={searchStateContainerStyle}>
          {error && (
            <div className={popupContentStyle}>
              <Message
                variant="error"
                body={"Er is een fout opgetreden. Probeer het later nog eens."}
              />
            </div>
          )}

          {!isLoading && !error && searchResults.length === 0 && !!value && (
            <Flex
              direction="column"
              gap="s"
              className={popupContentStyle}
              align="center"
            >
              <Text
                color="currentColor50"
                italic
                align="center"
                variant="bodyS"
              >
                Geen resultaten gevonden.
              </Text>
              <Card
                variant="primary"
                contentClassName={notFoundCTAContainerStyle}
                direction="vertical"
              >
                <Text align="center">{noResultsText}</Text>
                {cta}
              </Card>
            </Flex>
          )}

          {isLoading && !error && searchResults.length === 0 && (
            <Flex direction="column" gap="xs" className={popupContentStyle}>
              <Placeholder height={rem(48)} />
            </Flex>
          )}

          {!error && searchResults.length > 0 && (
            <SelectList
              value={selectedID ?? undefined}
              defaultHighlightedIndex={trapFocusMode === "immediate" ? 0 : -1}
              options={searchResults.map((result) => ({
                label: (
                  <Flex direction="column">
                    <Text>{getDisplayStreet(result)}</Text>
                    <Text variant="bodyS" color="currentColor50">
                      {result.city}
                      {result.zipcode && `, ${result.zipcode}`}
                    </Text>
                  </Flex>
                ),
                value: `${result.address_object_id}`,
              }))}
              onSelect={(option) => {
                setSelectedID(option.value as string);

                const selected = searchResults.find(
                  (result) => result.address_object_id === option.value,
                );

                if (selected) {
                  handleAddressSelect(selected);
                } else {
                  console.warn("Address not found", option.value);
                }
              }}
            />
          )}
        </div>
      }
      size="minimal"
      placement="bottomLeft"
      role="combobox"
      trapFocus
      trapFocusMode={trapFocusMode}
      escape
      {...popup}
    >
      <TextField
        value={value}
        className={mergeClasses(
          searchPropertySelectStyle({ width, selected: isSelected }),
          className,
        )}
        onChange={setValue}
        placeholder={placeholder}
        disabled={disabled || selectedLoading}
        onFocus={handleFocus}
        onBlur={() => setIsFocused(false)}
        label={
          (selected || selectedLoading) && (
            <SelectedProperty
              selected={selected}
              disabled={!!disabled}
              onDelete={handleDelete}
              loading={selectedLoading}
              trailing={selectedPropertyTrailing}
            />
          )
        }
        display={selected ? <Fragment /> : undefined}
        leading={
          !selected && (
            <Flex gap="xs">
              <Icon icon="search" size="s" color="placeholder" />
            </Flex>
          )
        }
        trailing={
          !selected &&
          value && (
            <IconButton
              ref={closeButtonRef}
              variant="opaque"
              icon="close"
              size="xxs"
              onClick={handleClear}
              disabled={disabled}
              rounded
            />
          )
        }
        proxy={
          (isLoading || selectedLoading) && (
            <ProgressBar variant="infinite" position="absolute" />
          )
        }
        onKeyDown={(e) => {
          if (["ArrowDown", "ArrowUp", "Tab"].includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
            setTrapFocusMode("immediate");
          }
        }}
        {...textFieldProps}
      />
    </Popup>
  );
};
